import {ChangeDetectionStrategy, Component, Input, OnInit} from '@angular/core';
import {UntypedFormControl, UntypedFormGroup} from '@angular/forms';
import {BaseComponent, TemplateFeature} from 'compob2b-libs';
import {format, isValid, parse, parseISO} from 'date-fns';
import {debounceTime, distinctUntilChanged, takeUntil} from 'rxjs/operators';
import {DamItemTemplateFeaturesService, TemplateFeatureValue} from '../../dam-item-template-features.service';

@Component({
  selector: 'app-tf-date',
  templateUrl: './tf-date.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TfDateComponent extends BaseComponent implements OnInit {
  @Input() feature: TemplateFeature;
  @Input() featureLink = false;

  form = new UntypedFormGroup({
    featureValue: new UntypedFormControl(null)
  });

  constructor(protected readonly templateFeatureService: DamItemTemplateFeaturesService) {
    super();
  }

  ngOnInit(): void {
    this.form.get('featureValue').valueChanges
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        takeUntil(this.unsubscribe),
      )
      .subscribe(data => {
        this.setFeature(this.formatDate(data));
        this.cdr.detectChanges();
      });

    this.setDefaultValue();

    this.templateFeatureService.isFeaturesModelReloaded()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(_ => {
        this.setDefaultValue();
        this.cdr.detectChanges();
      });
  }

  private setDefaultValue(): void {
    const featureModel = this.templateFeatureService.getFeatureModel();

    if (this.feature.id in featureModel) {
      this.featureValue.setValue(this.parseDate(featureModel[this.feature.id]), {emitEvent: false});
    }

    this.setDefaultFeatureValue(this.formatDate(this.featureValue.value));
  }

  private setFeature(featureValue: string | null): void {
    this.templateFeatureService.setFeature(
      this.feature.groupId,
      this.feature.id,
      featureValue,
      this.form.valid,
      this.isShowEmptyWarning()
    );
  }

  private setDefaultFeatureValue(featureValue: TemplateFeatureValue): void {
    this.templateFeatureService.setDefaultFeatureValue(
      this.feature.groupId,
      this.feature.id,
      featureValue,
      this.form.valid,
      this.isShowEmptyWarning()
    );
  }

  isEmpty(): boolean {
    return !this.featureValue.value;
  }

  isRequired(): boolean {
    return this.feature.required;
  }

  isShowEmptyWarning(): boolean {
    return this.isRequired() && this.isEmpty();
  }

  get featureValue(): any {
    return this.form.get('featureValue');
  }

  private parseDate(value: TemplateFeatureValue): Date | null {
    if (value == null || value === '') {
      return null;
    }
    if (value instanceof Date) {
      return isValid(value) ? value : null;
    }
    if (typeof value !== 'string') {
      return null;
    }

    const iso = parseISO(value);
    if (isValid(iso) && value.indexOf('-') !== -1) {
      return iso;
    }

    const dotted = parse(value, this.environment.dateFormat, new Date());
    return isValid(dotted) ? dotted : null;
  }

  private formatDate(value: Date | string | null): string | null {
    const date = value instanceof Date ? value : this.parseDate(value);
    return date && isValid(date) ? format(date, 'yyyy-MM-dd') : null;
  }
}
